import React, { createContext, useState, useEffect, useContext } from "react";
import {
  ProductBrandInterface,
  ProductCategoryInterface,
  ProductInterface
} from "../interfaces/product";
import { createInitialProductFilters } from "./helpers/createInitialProductFilters";
import useSessionStorage from "../hooks/useSessionStorage";

const noop = () => {};

export type ProductFilterType = "brands" | "categories";
export type ProductFilters = Record<ProductFilterType, string[]>;

type UpdateFilterParams = {
  filterType: ProductFilterType;
  id: string;
};

interface ProductFilterContext {
  addFilter: (params: UpdateFilterParams) => void;
  removeFilter: (params: UpdateFilterParams) => void;
  filteredProducts: ProductInterface[];
  availableFilters: {
    brands: ProductBrandInterface[];
    categories: ProductCategoryInterface[];
  };
  currentActiveFilters: ProductFilters;
  products: ProductInterface[];
  containerRef?: React.RefObject<HTMLDivElement>;
}

const initialState = {
  addFilter: noop,
  removeFilter: noop,
  filteredProducts: [],
  availableFilters: {
    brands: [],
    categories: []
  },
  currentActiveFilters: {
    brands: [],
    categories: []
  },
  products: [],
  containerRef: undefined
};

const ProductFilterContext = createContext<ProductFilterContext>(initialState);

interface ProductFilterContextProviderProps {
  children: JSX.Element;
  products: ProductInterface[];
  brands: ProductBrandInterface[];
  categories: ProductCategoryInterface[];
  enabledFilters: ProductFilterType[];
  mode?: "SINGLE" | "MULTI";
}

export const ProductFilterContextProvider = ({
  children,
  products,
  brands,
  categories,
  enabledFilters,
  mode = "MULTI"
}: ProductFilterContextProviderProps) => {
  const initialFilters = createInitialProductFilters(enabledFilters, products);
  const availableFilters = {
    brands: brands.filter(brand => initialFilters.brands.includes(brand.id)),
    categories: categories.filter(cat =>
      initialFilters.categories.includes(cat.id)
    )
  };
  const [currentActiveFilters, setCurrentActiveFilters] = useSessionStorage<
    ProductFilters
  >("productFilters", {
    brands: [],
    categories: []
  });
  const [filteredProducts, setFilteredProducts] = useState(products);

  const containerRef = React.useRef<HTMLDivElement>(null);

  const addFilter = ({ filterType, id }: UpdateFilterParams) => {
    if (
      id === "ALL" ||
      currentActiveFilters[filterType].length + 1 ===
        availableFilters[filterType].length
    ) {
      setCurrentActiveFilters({
        ...currentActiveFilters,
        [filterType]: []
      });
      return;
    }

    const newFilters =
      mode === "SINGLE" ? [id] : [...currentActiveFilters[filterType], id];

    setCurrentActiveFilters({
      ...currentActiveFilters,
      [filterType]: newFilters
    });
  };

  const removeFilter = ({ filterType, id }: UpdateFilterParams) => {
    if (id === "ALL") {
      setCurrentActiveFilters({
        ...currentActiveFilters,
        [filterType]: []
      });
      return;
    }
    setCurrentActiveFilters({
      ...currentActiveFilters,
      [filterType]: currentActiveFilters[filterType].filter(
        prevId => id !== prevId
      )
    });
  };

  useEffect(() => {
    let productsByCategory = products.filter(product => {
      const productCategories = product.categories.map(cat => cat.id);
      return currentActiveFilters.categories.some(cat =>
        productCategories.includes(cat)
      );
    });
    productsByCategory = productsByCategory.length
      ? productsByCategory
      : products;
    let productsByBrand = products.filter(product =>
      currentActiveFilters.brands.includes(product.brand.id)
    );
    productsByBrand = productsByBrand.length ? productsByBrand : products;
    if (!enabledFilters.includes("brands")) {
      productsByBrand = [];
    }
    if (!enabledFilters.includes("categories")) {
      productsByCategory = [];
    }
    const filteredProducts = [
      ...new Set([...productsByCategory, ...productsByBrand])
    ];
    setFilteredProducts(filteredProducts);
  }, [currentActiveFilters]);

  return (
    <ProductFilterContext.Provider
      value={{
        filteredProducts,
        currentActiveFilters,
        availableFilters,
        addFilter,
        removeFilter,
        products,
        containerRef
      }}
    >
      {children}
    </ProductFilterContext.Provider>
  );
};

export const useProductFilters = () => useContext(ProductFilterContext);
